import { useEffect, useRef, useState } from "react";

export interface PollState<T> {
  data: T | null;
  error: Error | null;
  lastUpdatedAt: number | null;
  loading: boolean;
}

/**
 * Poll an async function on an interval. Uses setTimeout chained after each
 * fetch resolves so slow responses don't stack up. Aborts inflight on unmount.
 */
export function usePoll<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  intervalMs: number,
  deps: ReadonlyArray<unknown>
): PollState<T> {
  const [state, setState] = useState<PollState<T>>({
    data: null,
    error: null,
    lastUpdatedAt: null,
    loading: true,
  });

  // Keep the latest fn in a ref so we don't retrigger the effect when the
  // caller passes an inline closure — deps drive re-runs explicitly.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;

    const tick = async () => {
      controller = new AbortController();
      try {
        const data = await fnRef.current(controller.signal);
        if (cancelled) return;
        setState({
          data,
          error: null,
          lastUpdatedAt: Date.now(),
          loading: false,
        });
      } catch (err) {
        if (cancelled) return;
        if ((err as { name?: string }).name === "AbortError") return;
        setState((s) => ({
          ...s,
          error: err instanceof Error ? err : new Error(String(err)),
          loading: false,
        }));
      } finally {
        if (!cancelled) {
          timer = setTimeout(tick, intervalMs);
        }
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (controller) controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);

  return state;
}
